import React from "react";
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { vi } from "date-fns/locale";
import { format, isValid, parseISO } from "date-fns";
import { LabeledField } from "@/__core/components/LabeledField";
import AddressFieldWrapper from "@/modules/addresses/components/AddressFieldWrapper";
import { sharedFormStyles } from "@/__core/styles/sharedFormStyles";
import { resolveEmployeeStatusId } from "@/modules/employees/utils/employeeStatus";

interface BasicInfoFormData {
  code?: string;
  fullName?: string;
  nickname?: string;
  date_of_birth?: string;
  status?: string;
  email?: string;
  gender?: string;
  phone?: string;
  address?: {
    address_line?: string;
    province?: string;
    ward?: string;
    free_address?: string;
  };
}

interface StatusOption {
  id: string;
  name: string;
  color?: string;
  is_default?: boolean;
}

interface BasicInfoProps {
  formData: BasicInfoFormData;
  errors: { [key: string]: string };
  canEdit: boolean;
  statusOptions?: StatusOption[];
  onChange: (field: string, value: any) => void;
}

const BasicInfo: React.FC<BasicInfoProps> = ({ formData, errors, canEdit, statusOptions = [], onChange }) => {
  const dateOfBirthValue = formData.date_of_birth ? parseISO(formData.date_of_birth) : null;

  const resolvedStatusOptions = statusOptions.length > 0
    ? statusOptions
    : [
        { id: "active", name: "Hoạt động", color: "#10b981", is_default: true },
        { id: "inactive", name: "Không hoạt động", color: "#ef4444", is_default: false },
        { id: "pending", name: "Chờ duyệt", color: "#f59e0b", is_default: false },
      ];

  const statusValue = resolveEmployeeStatusId(formData.status, "");
  const selectedStatus = resolvedStatusOptions.find((option) => option.id === statusValue);

  return (
    <Paper sx={{ ...sharedFormStyles.formCard, mb: 2 }}>
      <Typography sx={sharedFormStyles.sectionTitle}>Thông tin cơ bản</Typography>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label="Mã nhân viên" labelWidth={100}>
              <TextField
                fullWidth
                size="small"
                disabled
                value={formData.code || ""}
              />
            </LabeledField>

            <LabeledField label="Họ tên" required labelWidth={100}>
              <TextField
                fullWidth
                size="small"
                value={formData.fullName || ""}
                onChange={(e) => onChange("fullName", e.target.value)}
                error={!!errors.name}
                disabled={!canEdit}
                autoComplete="off"
              />
            </LabeledField>

            <LabeledField label="Bí Danh" labelWidth={100}>
              <TextField
                fullWidth
                size="small"
                value={formData.nickname || ""}
                onChange={(e) => onChange("nickname", e.target.value)}
                disabled={!canEdit}
                autoComplete="off"
              />
            </LabeledField>

            <LabeledField label="Ngày sinh" labelWidth={100}>
              <DatePicker
                value={dateOfBirthValue && isValid(dateOfBirthValue) ? dateOfBirthValue : null}
                onChange={(value) => onChange("date_of_birth", value && isValid(value) ? format(value, "yyyy-MM-dd") : "")}
                disabled={!canEdit}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label="Trạng thái" labelWidth={100}>
              <FormControl fullWidth size="small">
                <Select
                  value={statusValue}
                  onChange={(e) => onChange("status", e.target.value)}
                  disabled={!canEdit}
                  renderValue={(selected) => {
                    const selectedId = resolveEmployeeStatusId(selected, "");
                    const selectedOption =
                      resolvedStatusOptions.find((option) => option.id === selectedId) ||
                      selectedStatus;

                    if (!selectedOption) {
                      return selectedId || "";
                    }

                    return (
                      <Box component="span" sx={{ color: selectedOption.color || "inherit", fontWeight: 500 }}>
                        {selectedOption.name}
                      </Box>
                    );
                  }}
                >
                  {resolvedStatusOptions.map((statusOption) => (
                    <MenuItem key={statusOption.id} value={statusOption.id}>
                      <Box component="span" sx={{ color: statusOption.color || "inherit", fontWeight: 500 }}>
                          {statusOption.name}
                          {statusOption.is_default ? " (Mặc định)" : ""}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>

            <LabeledField label="Email" required labelWidth={100}>
              <TextField
                fullWidth
                size="small"
                type="email"
                value={formData.email || ""}
                onChange={(e) => onChange("email", e.target.value)}
                error={!!errors.email}
                disabled={!canEdit}
                autoComplete="off"
              />
            </LabeledField>

            <LabeledField label="Giới tính" labelWidth={100}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.gender || ""}
                  onChange={(e) => onChange("gender", e.target.value)}
                  disabled={!canEdit}
                >
                  <MenuItem value="">Chưa chọn</MenuItem>
                  <MenuItem value="male">Nam</MenuItem>
                  <MenuItem value="female">Nữ</MenuItem>
                  <MenuItem value="other">Khác</MenuItem>
                </Select>
              </FormControl>
            </LabeledField>

            <LabeledField label="Số điện thoại" labelWidth={100}>
              <TextField
                fullWidth
                size="small"
                value={formData.phone || ""}
                onChange={(e) => onChange("phone", e.target.value)}
                disabled={!canEdit}
                inputProps={{ inputMode: "tel" }}
              />
            </LabeledField>
          </Grid>
        </Grid>

        <AddressFieldWrapper
          initialAddress={formData.address || { address_line: "", province: "", ward: "", free_address: "" }}
          onAddressChange={(addressValue) => onChange("address", addressValue)}
          isEditMode={canEdit}
          labelWidth={100}
          styleDisplay="small"
        />
      </LocalizationProvider>
    </Paper>
  );
};

export default BasicInfo;
